import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import placeholder from '../assets/avatar-placeholder.png';

/* =========================
   FIELD & MISMATCH HELPERS
========================= */

const FIELD_MAP = {
    'Full Name': 'fullName',
    'First Name': 'givenName',
    'Last Name': 'familyName',
    'Date of Birth': 'dateOfBirth',
    'Gender': 'gender',
    'Phone Number': 'phoneNumber',
    'Email': 'email',
    'Nationality': 'nationality',
};

const ADDRESS_FIELD_MAP = {
    woreda: ['Woreda'],
    zone: ['Zone'],
    region: ['Region'],
};

const buildAddressMismatchLookup = (mismatches = []) => {
    const lookup = {};

    mismatches.forEach((m) => {
        Object.entries(ADDRESS_FIELD_MAP).forEach(([key, labels]) => {
            if (labels.includes(m.field)) {
                lookup[key] = m;
            }
        });
    });

    return lookup;
};

const buildMismatchLookup = (mismatches = []) =>
    mismatches.reduce((acc, m) => {
        const key = FIELD_MAP[m.field];
        if (key) acc[key] = m;
        return acc;
    }, {});

const tooltip = (m) =>
    `OFS: ${m.ofsValue || '-'} | Fayda: ${m.faydaValue || '-'}`;

/* =========================
   FIELD ROW
========================= */

const FieldRow = ({ label, value, mismatch, theme }) => {
    const isMismatch = Boolean(mismatch);

    return (
        <div
            className={`grid grid-cols-3 gap-4 py-2 border-b last:border-0 ${theme === 'ofs' ? 'border-blue-100' : 'border-green-100'
                }`}
            title={isMismatch ? tooltip(mismatch) : ''}
        >
            <div className="col-span-1 flex items-center gap-1">
                {isMismatch && <span className="w-2 h-2 rounded-full bg-red-500" />}
                <p
                    className={`text-xs font-semibold ${isMismatch
                        ? 'text-red-700'
                        : theme === 'ofs'
                            ? 'text-blue-700'
                            : 'text-green-700'
                        }`}
                >
                    {label}
                </p>
            </div>

            <div className="col-span-2">
                <p className={`text-sm font-medium ${isMismatch ? 'text-red-600' : 'text-gray-900'}`}>
                    {value || '-'}
                </p>
            </div>
        </div>
    );
};

/* =========================
   DATA SECTION
========================= */

const DataSection = ({ ofsData, faydaData, mismatches }) => {
    const mismatchLookup = buildMismatchLookup(mismatches);
    const addressMismatchLookup = buildAddressMismatchLookup(mismatches);

    const fields = [
        { key: 'fullName', label: 'Full Name' },
        { key: 'dateOfBirth', label: 'Date of Birth' },
        { key: 'gender', label: 'Gender' },
        { key: 'phoneNumber', label: 'Phone Number' },
        { key: 'email', label: 'Email' },
        { key: 'nationality', label: 'Nationality' },
    ];

    return (
        <Card className="mb-6">
            <h3 className="text-lg font-semibold mb-4 pb-3 border-b">
                Customer Verification Data
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* OFS */}
                <Card className="border-2 border-blue-200 bg-blue-50/50">
                    <h4 className="text-sm font-semibold text-blue-800 mb-4">
                        System Data (CBS)
                    </h4>

                    <div className="flex justify-center mb-4">
                        <img
                            src={ofsData.picture || placeholder}
                            alt="OFS"
                            className="w-32 h-32 rounded-full object-cover"
                        />
                    </div>

                    {fields.map((f) => (
                        <FieldRow
                            key={f.key}
                            label={f.label}
                            value={ofsData[f.key]}
                            mismatch={mismatchLookup[f.key]}
                            theme="ofs"
                        />
                    ))}

                    {ofsData?.address &&
                        Object.entries(ofsData.address).map(([k, v]) => {
                            const mismatch = addressMismatchLookup[k];
                            const isMismatch = !!mismatch;

                            return (
                                <div
                                    key={k}
                                    className={`grid grid-cols-3 gap-4 py-1 border-b last:border-0 ${isMismatch ? 'bg-red-50' : ''
                                        }`}
                                    title={isMismatch ? tooltip(mismatch) : ''}
                                >
                                    <div className="col-span-1 flex items-center gap-1">
                                        {isMismatch && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                        <p className={`text-xs font-semibold ${isMismatch ? 'text-red-700' : 'text-blue-600'
                                            }`}>
                                            {k}
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className={`text-sm ${isMismatch ? 'text-red-600' : 'text-gray-800'
                                            }`}>
                                            {v || '-'}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                </Card>

                {/* FAYDA */}
                <Card className="border-2 border-green-200 bg-green-50/50">
                    <h4 className="text-sm font-semibold text-green-800 mb-4">
                        Fayda API Data
                    </h4>

                    <div className="flex justify-center mb-4">
                        <img
                            src={faydaData.picture || placeholder}
                            alt="Fayda"
                            className="w-32 h-32 rounded-full object-cover"
                        />
                    </div>

                    {fields.map((f) => (
                        <FieldRow
                            key={f.key}
                            label={f.label}
                            value={faydaData[f.key]}
                            mismatch={mismatchLookup[f.key]}
                            theme="fayda"
                        />
                    ))}

                    {faydaData?.address &&
                        Object.entries(faydaData.address).map(([k, v]) => {
                            const mismatch = addressMismatchLookup[k];
                            const isMismatch = !!mismatch;

                            return (
                                <div
                                    key={k}
                                    className={`grid grid-cols-3 gap-4 py-1 border-b last:border-0 ${isMismatch ? 'bg-red-50' : ''
                                        }`}
                                    title={isMismatch ? tooltip(mismatch) : ''}
                                >
                                    <div className="col-span-1 flex items-center gap-1">
                                        {isMismatch && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                        <p className={`text-xs font-semibold ${isMismatch ? 'text-red-700' : 'text-blue-600'
                                            }`}>
                                            {k}
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className={`text-sm ${isMismatch ? 'text-red-600' : 'text-gray-800'
                                            }`}>
                                            {v || '-'}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                </Card>
            </div>
        </Card>
    );
};

/* =========================
   MAIN VIEW
========================= */

export default function CustomerVerificationView() {
    const navigate = useNavigate();
    const location = useLocation();
    const customer = location.state?.customer;
    console.log('CustomerVerificationView customer:', customer);

    if (!customer) {
        navigate('/approved');
        return null;
    }

    const ofsData = {
        fullName: customer.ofsData.fullName,
        dateOfBirth: customer.ofsData.dateOfBirth,
        gender: customer.ofsData.gender,
        phoneNumber: customer.ofsData.phone,
        email: customer.ofsData.email,
        nationality: customer.ofsData.nationality,
        address: customer.ofsData.address,
        picture: customer.ofsData.image,
        companyCode: customer.ofsData.companyCode,
    };

    const faydaData = {
        fullName: customer.faydaData.name,
        dateOfBirth: customer.faydaData.birthdate,
        gender: customer.faydaData.gender,
        phoneNumber: customer.faydaData.phoneNumber,
        email: customer.faydaData.email,
        picture: customer.faydaData.picture,
        nationality: customer.faydaData.nationality,
        address: customer.faydaData.address,
    };

    const isApproved = customer.status === 'APPROVED';
    const isApprovedAndOverridden = customer.downstreamSyncType === 'APPROVE_AND_OVERRIDE';

    return (
        <Layout>
            <div className="max-w-7xl mx-auto">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                </Button>

                <h1 className="text-3xl font-bold mb-2">{ofsData.fullName}{' ('}{ofsData.companyCode}{')'}{isApproved ? ' (Approved)' : ' (Rejected)'}</h1>

                <DataSection
                    ofsData={ofsData}
                    faydaData={faydaData}
                    mismatches={customer.mismatchedFields}
                />
                {!isApproved && !isApprovedAndOverridden ? (
                    <Card className="mb-6 border border-red-200 bg-red-50">
                        <div className="flex gap-3">
                            <XCircle className="text-red-600 w-5 h-5 mt-1" />
                            <div>
                                <p className="font-semibold text-red-700">
                                    Rejection Reason
                                </p>
                                <p className="text-sm text-red-600 mt-1">
                                    {customer.decisionReason ||
                                        customer.rejectionReason ||
                                        'No reason provided'}
                                </p>
                            </div>
                        </div>
                    </Card>
                ) : isApprovedAndOverridden ? (
                    <Card className="mb-6 border border-green-200 bg-green-50">
                        <div className="flex gap-3">
                            <CheckCircle className="text-green-600 w-5 h-5 mt-1" />
                            <div>
                                <p className="font-semibold text-green-700">
                                    Approved with Override
                                </p>
                                <p className="text-sm text-green-600 mt-1">
                                    {customer.approvalReason ||
                                        'No reason provided'}
                                </p>
                            </div>
                        </div>
                    </Card>
                ) : null}

                <Card className="mt-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-500">Reviewed By</span>
                            <p className="font-medium">
                                {customer.reviewedBy
                                    ? `${customer.reviewedBy.firstName} ${customer.reviewedBy.lastName}`
                                    : '-'}
                            </p>
                        </div>

                        <div>
                            <span className="text-gray-500">Reviewed On</span>
                            <p className="font-medium">
                                {customer.reviewedAt
                                    ? new Date(customer.reviewedAt).toLocaleString()
                                    : '-'}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>
        </Layout>
    );
}
